/* =========================================================================
   LAYER'D Toolkit — THE DESIGN SECTION (r199): inspiration + palette.
   File: timeline-hub/assets/learn-clientfile-design.js (theme asset).
   Loaded on the Planner page before learn-clientfile-print.js.

   Two new things on a client file, both attached per booking:

     design.palette  [{ hex, name }]         — small, SYNCED
     design.inspo    [{ id, w, h, thumb }]   — metadata + tiny cover, SYNCED
                     the full image itself   — IndexedDB, DEVICE-LOCAL

   THE STORAGE DECISION (Mariya asked for advice).
   localStorage holds ~5MB per origin for the WHOLE suite - bookings,
   recipes, combos, pans, settings. One phone photo is 3-6MB. Two dropped
   images would take the entire quota down and every tool with it, so the
   full images cannot live there and cannot ride the existing cloud sync.
   What we do instead:
     · the original is downscaled to 1600px on its long edge, re-encoded
       JPEG q0.82 (~250-450KB) and stored in IndexedDB on the device that
       dropped it. IndexedDB is per-device but has room.
     · a 240px cover thumbnail (~12-20KB) is base64'd into the booking, so
       it syncs with the file and every device can SHOW the design.
     · the palette is hexes and names - bytes. It always syncs, which is the
       part that matters on a consultation call from another device.
     · a tile whose full image is not on this device renders from the thumb
       with .ldc-inspo__tile--remote, which says "on your other device"
       rather than pretending to be broken.
   If images ever need to be truly portable, the answer is object storage on
   the LAYER'D account, not localStorage - out of scope here, and the shape
   above is forward-compatible with it (swap the IndexedDB read for a URL).
   ========================================================================= */
(function () {
  'use strict';

  var DB_NAME = 'layerd-inspo';
  var STORE = 'images';
  var MAX_EDGE = 1600;
  var THUMB_EDGE = 240;      /* the palette-sampling thumb: small = fast k-means */
  /* 2026-09-17 (Mariya: a phone photo looked pixelated + small on her computer).
     Only this cover is SYNCED (the full 1600px image stays device-local in
     IndexedDB), so on another device it IS the picture. 240px was too small for
     the hero + lightbox - it upscaled to mush. 900px fills them cleanly and is
     still only ~90-160KB per photo. (Applies to photos added from here on;
     already-attached 240px covers stay as they are until re-uploaded.) */
  var COVER_EDGE = 900;
  var COVER_Q = 0.8;
  var JPEG_Q = 0.82;
  var MAX_PER_FILE = 12;

  /* ---------------------------------------------------------- IndexedDB */

  function openDb() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) return reject(new Error('no indexeddb'));
      var rq = indexedDB.open(DB_NAME, 1);
      rq.onupgradeneeded = function () {
        var db = rq.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      rq.onsuccess = function () { resolve(rq.result); };
      rq.onerror = function () { reject(rq.error); };
    });
  }

  function idbPut(key, blob) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(blob, key);
        tx.oncomplete = function () { resolve(true); };
        tx.onerror = function () { reject(tx.error); };
      });
    });
  }

  function idbGet(key) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE, 'readonly');
        var rq = tx.objectStore(STORE).get(key);
        rq.onsuccess = function () { resolve(rq.result || null); };
        rq.onerror = function () { reject(rq.error); };
      });
    });
  }

  function idbDel(key) {
    return openDb().then(function (db) {
      return new Promise(function (resolve) {
        var tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(key);
        tx.oncomplete = function () { resolve(true); };
        tx.onerror = function () { resolve(false); };
      });
    });
  }

  /* ------------------------------------------------------------- images */

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('decode failed')); };
      img.src = src;
    });
  }

  function fileToDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var fr = new FileReader();
      fr.onload = function () { resolve(fr.result); };
      fr.onerror = function () { reject(fr.error); };
      fr.readAsDataURL(file);
    });
  }

  function drawScaled(img, maxEdge) {
    var scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    var w = Math.max(1, Math.round(img.width * scale));
    var h = Math.max(1, Math.round(img.height * scale));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    c.getContext('2d').drawImage(img, 0, 0, w, h);
    return c;
  }

  function canvasBlob(canvas, q) {
    return new Promise(function (resolve) {
      if (canvas.toBlob) canvas.toBlob(function (b) { resolve(b); }, 'image/jpeg', q);
      else resolve(null);
    });
  }

  /* ------------------------------------------- the palette, from a photo

     k-means in RGB over the downscaled pixels. No library, ~15ms on a
     240px thumb. Deliberate choices:
       · sampled from the THUMB, not the full image - k-means on 2MP is
         seconds of jank for an identical answer.
       · near-white and near-black pixels are dropped before clustering
         (paper, shadow and blown highlights otherwise win every cluster
         and hand back five greys).
       · k-means++ seeding, so the same photo gives the same five colours
         every time. A palette that reshuffles on re-run reads as a bug.
       · returned lightest-first: how a baker reads a palette out loud.
  */

  function samplePixels(canvas) {
    var ctx = canvas.getContext('2d');
    var d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    var out = [];
    for (var i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) continue;
      var r = d[i], g = d[i + 1], b = d[i + 2];
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      if (mx > 244 && mn > 236) continue;      /* paper / blown highlight */
      if (mx < 26) continue;                    /* shadow */
      out.push([r, g, b]);
    }
    return out;
  }

  function dist2(a, b) {
    var dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
    return dr * dr + dg * dg + db * db;
  }

  function kmeans(pts, k, iters) {
    if (!pts.length) return [];
    k = Math.min(k, pts.length);
    /* k-means++ seeding, deterministic: first centre is the pixel nearest
       the set's mean, then farthest-point selection. */
    var mean = [0, 0, 0], i, j;
    for (i = 0; i < pts.length; i++) { mean[0] += pts[i][0]; mean[1] += pts[i][1]; mean[2] += pts[i][2]; }
    mean = [mean[0] / pts.length, mean[1] / pts.length, mean[2] / pts.length];
    var centres = [pts.reduce(function (best, p) {
      return dist2(p, mean) < dist2(best, mean) ? p : best;
    }, pts[0]).slice()];
    while (centres.length < k) {
      var far = null, farD = -1;
      for (i = 0; i < pts.length; i++) {
        var d = Infinity;
        for (j = 0; j < centres.length; j++) d = Math.min(d, dist2(pts[i], centres[j]));
        if (d > farD) { farD = d; far = pts[i]; }
      }
      centres.push(far.slice());
    }
    var counts = new Array(k);
    for (var it = 0; it < (iters || 12); it++) {
      var sums = [];
      for (j = 0; j < k; j++) { sums.push([0, 0, 0]); counts[j] = 0; }
      for (i = 0; i < pts.length; i++) {
        var bi = 0, bd = Infinity;
        for (j = 0; j < k; j++) { var dd = dist2(pts[i], centres[j]); if (dd < bd) { bd = dd; bi = j; } }
        sums[bi][0] += pts[i][0]; sums[bi][1] += pts[i][1]; sums[bi][2] += pts[i][2];
        counts[bi]++;
      }
      for (j = 0; j < k; j++) {
        if (!counts[j]) continue;
        centres[j] = [sums[j][0] / counts[j], sums[j][1] / counts[j], sums[j][2] / counts[j]];
      }
    }
    return centres.map(function (c, ix) {
      return { rgb: c.map(Math.round), weight: counts[ix] || 0 };
    }).filter(function (c) { return c.weight > 0; });
  }

  function hexOf(rgb) {
    return '#' + rgb.map(function (n) {
      var s = Math.max(0, Math.min(255, n | 0)).toString(16);
      return s.length === 1 ? '0' + s : s;
    }).join('').toUpperCase();
  }

  function luma(rgb) { return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]; }

  /* ------------------------------------------ naming a colour (2026-09-09)

     Mariya was typing names in by hand. nameOf() returns the nearest named
     colour from a curated wedding/cake vocabulary, measured in CIE Lab so
     "nearest" is nearest to the eye rather than nearest in RGB numbers.
     Names are suggestions: the file keeps whatever the baker types over
     them. The brand tones are in the list so they name themselves. */
  var NAMED = [
    ['Ivory', '#FFFFF0'], ['Cream', '#FFFDD0'], ['Champagne', '#F7E7CE'], ['Vanilla', '#F3E5AB'], ['Buttercream', '#FFF3C4'],
    ['Eggshell', '#F0EAD6'], ['Linen', '#FAF0E6'], ['Porcelain', '#FCFBF8'], ['White', '#FFFFFF'], ['Pearl', '#EAE0C8'],
    ['Blush', '#F4C2C2'], ['Ballet pink', '#F7CAC9'], ['Dusty rose', '#C9A9A6'], ['Rose', '#E8A0BF'], ['Powder pink', '#FFD1DC'],
    ['Bubblegum', '#FFC1CC'], ['Hot pink', '#FF69B4'], ['Fuchsia', '#D33F8C'], ['Mauve', '#B784A7'], ['Old rose', '#C08081'],
    ['Salmon', '#FA8072'], ['Coral', '#FF7F50'], ['Peach', '#FFDAB9'], ['Apricot', '#FBCEB1'], ['Nude', '#E3BC9A'],
    ['Terracotta', '#E2725B'], ['Rust', '#B7410E'], ['Burnt orange', '#CC5500'], ['Tangerine', '#F28500'], ['Marigold', '#EAA221'],
    ['Mustard', '#E1AD01'], ['Gold', '#D4AF37'], ['Champagne gold', '#BCA069'], ['Honey', '#EBB434'], ['Butter', '#FFE57F'],
    ['Lemon', '#FFF44F'], ['Pale yellow', '#FFFFA7'],
    ['Burgundy', '#800020'], ['Wine', '#722F37'], ['Merlot', '#73343A'], ['Cranberry', '#9E003A'], ['Cherry', '#DE3163'],
    ['Crimson', '#DC143C'], ['Scarlet', '#FF2400'], ['Brick', '#CB4154'], ['Maroon', '#800000'], ['Plum', '#8E4585'],
    ['Aubergine', '#4C3A50'], ['Berry', '#8F3B76'],
    ['Lavender', '#E6E6FA'], ['Lilac', '#C8A2C8'], ['Wisteria', '#C9A0DC'], ['Periwinkle', '#CCCCFF'], ['Violet', '#7F00FF'],
    ['Amethyst', '#9966CC'], ['Orchid', '#DA70D6'], ['Grape', '#6F2DA8'], ['Eggplant', '#614051'], ['Dusty lavender', '#AC9EC0'],
    ['Mulberry', '#C54B8C'],
    ['Dusty blue', '#8FA3BF'], ['Powder blue', '#B0E0E6'], ['Baby blue', '#89CFF0'], ['Sky blue', '#87CEEB'], ['Cornflower', '#6495ED'],
    ['Steel blue', '#4682B4'], ['Slate blue', '#6A5ACD'], ['French blue', '#0072BB'], ['Royal blue', '#4169E1'], ['Navy', '#000080'],
    ['Midnight', '#191970'], ['Teal', '#008080'], ['Peacock', '#005F69'], ['Turquoise', '#40E0D0'], ['Aqua', '#7FDBFF'],
    ["Robin's egg", '#96DED1'], ['Denim', '#1560BD'],
    ['Sage', '#9CAF88'], ['Eucalyptus', '#6F7D62'], ['Olive', '#7A8450'], ['Moss', '#8A9A5B'], ['Fern', '#4F7942'],
    ['Forest', '#228B22'], ['Emerald', '#50C878'], ['Mint', '#B6E5D8'], ['Seafoam', '#93E9BE'], ['Pistachio', '#93C572'],
    ['Lime', '#BFFF00'], ['Jade', '#00A86B'], ['Hunter green', '#355E3B'], ['Pine', '#01796F'], ['Celadon', '#ACE1AF'],
    ['Taupe', '#B9A281'], ['Mocha', '#967969'], ['Latte', '#C5A57F'], ['Caramel', '#C68E17'], ['Toffee', '#B4712B'],
    ['Chocolate', '#7B3F00'], ['Espresso', '#3C2218'], ['Walnut', '#5C4033'], ['Chestnut', '#954535'], ['Cinnamon', '#D2691E'],
    ['Camel', '#C19A6B'], ['Sand', '#C2B280'], ['Beige', '#F5F5DC'], ['Oatmeal', '#D9CAB3'], ['Mushroom', '#BDB0A0'],
    ['Stone', '#928E85'], ['Greige', '#B8B0A3'],
    ['Silver', '#C0C0C0'], ['Dove grey', '#CCC8C4'], ['Ash', '#B2BEB5'], ['Pewter', '#8E9291'], ['Slate', '#708090'],
    ['Charcoal', '#36454F'], ['Graphite', '#383838'], ['Ink', '#2A2138'], ['Black', '#000000'], ['Onyx', '#353839'],
    ['Rose gold', '#B76E79'], ['Copper', '#B87333'], ['Bronze', '#CD7F32'], ['Brass', '#B5A642']
  ];
  var NAMED_LAB = null;
  function hexToRgb(hex) {
    var h = String(hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (h.length !== 6 || isNaN(n)) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToLab(rgb) {
    var f = rgb.map(function (v) { v = v / 255; return v > 0.04045 ? Math.pow((v + 0.055) / 1.055, 2.4) : v / 12.92; });
    var x = (f[0] * 0.4124 + f[1] * 0.3576 + f[2] * 0.1805) / 0.95047;
    var y = (f[0] * 0.2126 + f[1] * 0.7152 + f[2] * 0.0722);
    var z = (f[0] * 0.0193 + f[1] * 0.1192 + f[2] * 0.9505) / 1.08883;
    function g(t) { return t > 0.008856 ? Math.cbrt(t) : (7.787 * t + 16 / 116); }
    x = g(x); y = g(y); z = g(z);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  }
  function nameOf(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return '';
    if (!NAMED_LAB) NAMED_LAB = NAMED.map(function (e) { return { name: e[0], lab: rgbToLab(hexToRgb(e[1])) }; });
    var lab = rgbToLab(rgb), best = '', bd = Infinity;
    NAMED_LAB.forEach(function (e) {
      var dl = lab[0] - e.lab[0], da = lab[1] - e.lab[1], db = lab[2] - e.lab[2];
      var d = dl * dl + da * da + db * db;
      if (d < bd) { bd = d; best = e.name; }
    });
    return best;
  }

  /* the whole extraction, for a File or a data URL — every colour arrives named */
  function paletteFrom(source, k) {
    var p = (typeof source === 'string') ? Promise.resolve(source) : fileToDataUrl(source);
    return p.then(loadImage).then(function (img) {
      var thumb = drawScaled(img, THUMB_EDGE);
      var pts = samplePixels(thumb);
      var cs = kmeans(pts, k || 5, 12);
      cs.sort(function (a, b) { return luma(b.rgb) - luma(a.rgb); });
      return cs.map(function (c) { var hx = hexOf(c.rgb); return { hex: hx, name: nameOf(hx) }; });
    });
  }

  /* ------------------------------------------------- attaching an image */

  function attach(bookingId, file) {
    if (!/^image\//.test(file.type)) return Promise.reject(new Error('not an image'));
    var id = 'i' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    var key = bookingId + '/' + id;
    return fileToDataUrl(file).then(loadImage).then(function (img) {
      var full = drawScaled(img, MAX_EDGE);
      var thumbCanvas = drawScaled(img, COVER_EDGE);
      return canvasBlob(full, JPEG_Q).then(function (blob) {
        var record = {
          id: id,
          w: img.width,
          h: img.height,
          thumb: thumbCanvas.toDataURL('image/jpeg', COVER_Q),
          device: deviceTag(),
          at: Date.now()
        };
        return (blob ? idbPut(key, blob) : Promise.resolve(false))
          .catch(function () { return false; })
          .then(function () { return record; });
      });
    });
  }

  /* full image if this device has it, else the synced thumb */
  function displaySrc(bookingId, record) {
    return idbGet(bookingId + '/' + record.id).then(function (blob) {
      if (blob) return { src: URL.createObjectURL(blob), local: true };
      return { src: record.thumb, local: false };
    }).catch(function () {
      return { src: record.thumb, local: false };
    });
  }

  function remove(bookingId, record) {
    return idbDel(bookingId + '/' + record.id);
  }

  /* which device dropped it - the copy on a remote tile names it */
  function deviceTag() {
    try {
      var t = localStorage.getItem('layerd-device-tag');
      if (t) return t;
      var made = /iPad/.test(navigator.userAgent) ? 'iPad'
        : /iPhone|Android/.test(navigator.userAgent) ? 'phone' : 'computer';
      localStorage.setItem('layerd-device-tag', made);
      return made;
    } catch (e) { return 'another device'; }
  }

  /* --------------------------------------------------------- persistence

     design lives ON the booking, so it rides the existing cloud sync and
     LdCakeFile stays the only writer of a booking. */

  function readDesign(booking) {
    var d = (booking && booking.design) || {};
    return {
      palette: Array.isArray(d.palette) ? d.palette : [],
      inspo: Array.isArray(d.inspo) ? d.inspo : [],
      finish: d.finish || '',
      /* 2026-09-20 (Mariya): finishing & décor work — labour-bearing elements
         (fondant exterior, intricate piping, custom detailing…) the Planner must
         reserve time for even when they carry no price. Each: {id, label, band}
         with band light | medium | heavy. Priced décor (flowers/painting/etc.)
         still comes from Cost & price; this is for everything else. */
      labour: Array.isArray(d.labour) ? d.labour : [],
      /* UX 2026-09-08: which inspo image is the cake file's hero (shown on the
         Studio's active-cake header). An id into inspo, or null. */
      hero: d.hero || null
    };
  }
  /* rough time each band reserves in the schedule (hours) — kept here so the
     Design section and the Planner read one definition */
  var LABOUR_BANDS = { light: 1, medium: 4, heavy: 8 };
  function labourHours(band) { return LABOUR_BANDS[band] || LABOUR_BANDS.medium; }

  function writeDesign(bookingId, design) {
    var F = window.LdCakeFile;
    if (!F || !F.updateBooking) return false;
    /* trim before writing: the thumbs are the only heavy thing on the
       booking and MAX_PER_FILE keeps a file's synced payload ~200KB. */
    var inspo = (design.inspo || []).slice(0, MAX_PER_FILE);
    /* hero survives only while its image still exists — a removed hero clears */
    var hero = (design.hero && inspo.some(function (x) { return x.id === design.hero; })) ? design.hero : null;
    var labour = (design.labour || []).slice(0, 20).map(function (x, i) {
      var band = (x && LABOUR_BANDS[x.band]) ? x.band : 'medium';
      return { id: (x && x.id) || ('dl' + i), label: ((x && x.label) || 'Design work').toString().slice(0, 60), band: band };
    });
    var d = {
      palette: (design.palette || []).slice(0, 8),
      inspo: inspo,
      finish: (design.finish || '').slice(0, 600),
      labour: labour,
      hero: hero
    };
    return F.updateBooking(bookingId, function (b) { b.design = d; });
  }

  window.LAYERD = window.LAYERD || {};
  window.LAYERD.fileDesign = {
    paletteFrom: paletteFrom,
    attach: attach,
    displaySrc: displaySrc,
    remove: remove,
    readDesign: readDesign,
    writeDesign: writeDesign,
    hexOf: hexOf,
    nameOf: nameOf,
    MAX_PER_FILE: MAX_PER_FILE,
    deviceTag: deviceTag,
    /* finishing & décor labour — shared definitions for the Design section
       (studio spine) and the Planner. Presets are common labour NOT already
       set from Cost & price (flowers / painting / sculpting / chocolate décor
       plan themselves from their priced elements), so nothing double-schedules. */
    LABOUR_BANDS: LABOUR_BANDS,
    labourHours: labourHours,
    labourPresets: [
      { id: 'fondant-ext', label: 'Fondant decoration', band: 'heavy' },
      { id: 'piping', label: 'Intricate piping', band: 'medium' },
      { id: 'texture', label: 'Textured / palette-knife finish', band: 'light' },
      { id: 'detailing', label: 'Fine hand-detailing', band: 'medium' },
      { id: 'stencil', label: 'Stencilling', band: 'light' },
      { id: 'ruffles', label: 'Ruffles / fabric effect', band: 'medium' }
    ]
  };
})();
